import { useId } from "@floating-ui/react"
import classNames from "classnames"
import { Children, isValidElement } from "react"
import { Icon } from "../../Icon"
import { validateDisplayNameChild } from "../../utils/validateDisplayNameChild"
import { SelectEmptyState } from "../base"
import { SelectContext } from "../Select/SelectContext"
import { SelectList } from "../Select/SelectList"
import { useSelect } from "../Select/useSelect"
import { useSelectControl } from "../Select/useSelectControl"
import { useInputWithSelectContext } from "./InputWithSelect"
import { InputWithSelectSelectProps } from "./types"

export const InputWithSelectSelect = ({
  children,
  emptyState = <SelectEmptyState />,
  value: valueProp,
  label,
  name: nameProp,
  onFocus,
  onChange,
  onBlur,
  placeholder,
  selectedValueDisplay,
}: InputWithSelectSelectProps) => {
  const id = useId()
  const { disabled } = useInputWithSelectContext()

  const inputNodes = Children.toArray(children).filter(child => {
    return isValidElement(child) && validateDisplayNameChild(child, "Select.Input")
  })

  const select = useSelect({
    containsInput: inputNodes.length > 0,
    onChange,
    value: valueProp,
    disabled,
    id,
    name: nameProp,
    listStyles: {
      width: "fit-content",
      minWidth: "240px",
    },
  })
  const selectControl = useSelectControl({
    select,
    placeholder,
    UNSAFE_selectedValueDisplay: selectedValueDisplay,
    optionNodes: children,
  })
  const _classNameIcon = classNames("transition-all", { "rotate-180": select.open })

  return (
    <SelectContext.Provider
      value={{
        ...select,
        disabled,
        onBlur,
        onChange,
        onFocus,
      }}
    >
      <label className="sr-only" id={`label-${id}`}>
        {label}
      </label>
      <div
        className={
          "flex items-center justify-between gap-2 px-4 text-left outline-none transition-all focus:border-primary-base"
        }
        aria-disabled={disabled}
        {...selectControl.getSelectControlProps()}
      >
        <div id={`select-control-${id}`} role="presentation" className="w-full text-sm">
          {selectControl.getSelectDisplay()}
        </div>
        <Icon aria-hidden name="ChevronDown" size="sm" className={_classNameIcon} />
      </div>
      <SelectList emptyState={emptyState}>{children /* Select.Option[] */}</SelectList>
    </SelectContext.Provider>
  )
}
